import type { KitchenLine } from './kitchen-items'

/**
 * A ticket's doobjednávky, as the kitchen draws them - `FRONTEND.md` §9.670, FB-995, owner #20.
 *
 * **The owner**: *"ak sa spraví doobjednavka druhé kolo tak to prvé by nemuselo vôbec ukazovať ak je to možné ( ak
 * bolo samozrejme dokončené v kuchyni a vynesené na restike ? - že vyškrtal ich ? ..."*
 *
 * A second round exists only after the kitchen had finished the first - `OrderUpdateService.addRound` reopens a
 * finished order and otherwise joins the round already on the pass - so an earlier round is food that left the
 * kitchen. Once the waiter has also carried every line of it out, it is on the table and the cook has nothing to
 * read in it: it folds into one line, *Kolo N - vynesené*.
 *
 * Pure and separate from the card for `item-name.ts`'s reason: which lines a cook is shown is a rule, and a rule
 * that only exists inside a component is a rule nobody can check.
 */

/** What Otvorené stoly's *Vyniesť* writes - `served-lines.ts`. Anything else, absent included, is not served. */
const SERVED = 'served'

export interface KitchenRound {
  round: number
  lines: KitchenLine[]
  /** Earlier than the newest round on this ticket, so drawn faded. */
  earlier: boolean
  /** An earlier round whose every line was carried out: drawn as one line and counted as nothing. */
  folded: boolean
}

export function roundOf(line: KitchenLine): number {
  return line.kitchenRound ?? 1
}

/** Whether the waiter has carried this line out. An absent status is not served - `table-status.ts` reads it so. */
export function isServed(line: KitchenLine): boolean {
  return line.servedStatus === SERVED
}

/**
 * The ticket's rounds, oldest first, each saying whether it is folded.
 *
 * **The newest round is never folded** - it is the work in progress, and a served newest round is a state the
 * board does not reach (a finished ticket is not on it). **A round with one line still on the pass is drawn
 * whole**, faded as before: half a round folded would hide the half that is not on the table.
 */
export function kitchenRounds(lines: readonly KitchenLine[]): KitchenRound[] {
  const rounds = [...new Set(lines.map(roundOf))].sort((first, second) => first - second)
  const latest = rounds[rounds.length - 1] ?? 1
  return rounds.map((round) => {
    const roundLines = lines.filter((line) => roundOf(line) === round)
    const earlier = round < latest
    return {
      round,
      lines: roundLines,
      earlier,
      folded: earlier && roundLines.length > 0 && roundLines.every(isServed),
    }
  })
}

/**
 * How many pieces the ticket still shows - the strip's *N pol.*
 *
 * Counted over what is drawn, so a folded round adds nothing: the figure says how much is left to read, and a
 * count that included the food already on the table would contradict the card under it.
 */
export function shownItemCount(rounds: readonly KitchenRound[]): number {
  return rounds
    .filter((round) => !round.folded)
    .reduce(
      (total, round) => total + round.lines.reduce((sum, line) => sum + (line.quantity ?? 1), 0),
      0,
    )
}

/**
 * The card's own key for a line - per order, **per round**, per entry, per slot. Since §9.811 (owner O157) the tap
 * itself is stored on the line's row (`kitchenProgress`), and this keys the card's elements.
 *
 * **The round is in the key because the entry index restarts in every round.** It was `orderId-entryIndex`, so
 * round 2's first line shared a key with round 1's - and round 1's taps were still stored when the ticket came
 * back, so the new round opened already marked as being made or made. Legacy keys it the same way
 * (`kitchen.tsx:633`), which is a port that matched legacy and was still wrong.
 */
export function itemKey(
  orderId: number,
  round: number,
  entryIndex: number,
  slotIndex?: number,
): string {
  const base = `${orderId}-r${round}-${entryIndex}`
  return slotIndex === undefined ? base : `${base}-${slotIndex}`
}
